import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { todosApi, categoriesApi, listsApi } from '../services/api';
import { useAuth } from './AuthContext';
import type {
  Todo,
  Category,
  List,
  TodoCreateInput,
  TodoUpdateInput,
  CategoryCreateInput,
  CategoryUpdateInput,
  ListCreateInput,
  ListUpdateInput,
  PaginationInfo,
} from '../types';

interface TodoContextType {
  todos: Todo[];
  categories: Category[];
  lists: List[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  selectedCategory: string | null;
  selectedList: string | null;
  viewMode: 'list' | 'grid';
  setSelectedCategory: (id: string | null) => void;
  setSelectedList: (id: string | null) => void;
  setViewMode: (mode: 'list' | 'grid') => void;
  fetchTodos: (params?: { category?: string; listId?: string; completed?: boolean; page?: number }) => Promise<void>;
  fetchCategories: (listId?: string) => Promise<void>;
  fetchLists: () => Promise<void>;
  createTodo: (data: TodoCreateInput) => Promise<Todo>;
  duplicateTodo: (id: string, listId: string, category?: string | null) => Promise<Todo>;
  updateTodo: (id: string, data: TodoUpdateInput) => Promise<Todo>;
  deleteTodo: (id: string) => Promise<void>;
  toggleTodo: (id: string) => Promise<Todo>;
  shareTodo: (id: string, email: string, permission: 'view' | 'edit') => Promise<void>;
  unshareTodo: (id: string, userId: string) => Promise<void>;
  reorderTodos: (todos: Array<{ id: string; position: number }>) => Promise<void>;
  addSubtask: (todoId: string, title: string) => Promise<Todo>;
  toggleSubtask: (todoId: string, subtaskId: string) => Promise<Todo>;
  deleteSubtask: (todoId: string, subtaskId: string) => Promise<Todo>;
  renameSubtask: (todoId: string, subtaskId: string, title: string) => Promise<Todo>;
  reorderSubtasks: (todoId: string, subtasks: Array<{ id: string; position: number }>) => Promise<void>;
  uploadTodoImage: (todoId: string, file: File) => Promise<Todo>;
  deleteTodoImage: (todoId: string, filename: string) => Promise<Todo>;
  createCategory: (data: CategoryCreateInput) => Promise<Category>;
  updateCategory: (id: string, data: CategoryUpdateInput) => Promise<Category>;
  deleteCategory: (id: string) => Promise<void>;
  shareCategory: (id: string, email: string, permission: 'view' | 'edit') => Promise<void>;
  unshareCategory: (id: string, userId: string) => Promise<void>;
  createList: (data: ListCreateInput) => Promise<List>;
  updateList: (id: string, data: ListUpdateInput) => Promise<List>;
  deleteList: (id: string) => Promise<void>;
  shareList: (id: string, email: string, permission: 'view' | 'edit') => Promise<void>;
  unshareList: (id: string, userId: string) => Promise<void>;
  allTags: string[];
  handleSSEEvent: (type: string, data: unknown) => void;
  clearError: () => void;
}

const TodoContext = createContext<TodoContextType | null>(null);

export function TodoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [lists, setLists] = useState<List[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(() => {
    const saved = localStorage.getItem('todo-selected-category');
    return saved || null;
  });
  const [selectedList, setSelectedList] = useState<string | null>(() => {
    const saved = localStorage.getItem('todo-selected-list');
    return saved || null;
  });
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    const saved = localStorage.getItem('todo-view-mode');
    return (saved as 'list' | 'grid') || 'list';
  });

  // Only use user's default view preference if no localStorage value exists
  useEffect(() => {
    const saved = localStorage.getItem('todo-view-mode');
    if (!saved && user?.preferences?.defaultView) {
      setViewMode(user.preferences.defaultView);
    }
  }, [user?.preferences?.defaultView]);

  useEffect(() => {
    localStorage.setItem('todo-view-mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (selectedList) {
      localStorage.setItem('todo-selected-list', selectedList);
    } else {
      localStorage.removeItem('todo-selected-list');
    }
  }, [selectedList]);

  useEffect(() => {
    if (selectedCategory) {
      localStorage.setItem('todo-selected-category', selectedCategory);
    } else {
      localStorage.removeItem('todo-selected-category');
    }
  }, [selectedCategory]);

  const fetchTodos = useCallback(
    async (params?: { category?: string; listId?: string; completed?: boolean; page?: number }) => {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const response = await todosApi.list({
          ...params,
          category: params?.category || selectedCategory || undefined,
          listId: params?.listId || selectedList || undefined,
          limit: 100,
        });
        if (response.success && response.data) {
          setTodos(response.data.todos);
          setPagination(response.data.pagination);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch todos');
      } finally {
        setLoading(false);
      }
    },
    [user, selectedCategory, selectedList]
  );

  const fetchCategories = useCallback(async (listId?: string) => {
    if (!user) return;
    try {
      // Fetch all categories (don't filter by selectedList - sidebar needs all of them)
      const response = await categoriesApi.list({ listId });
      if (response.success && response.data) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    }
  }, [user]);

  const fetchLists = useCallback(async () => {
    if (!user) return;
    try {
      const response = await listsApi.list();
      if (response.success && response.data) {
        setLists(response.data.lists);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch lists');
    }
  }, [user]);

  const fetchTags = useCallback(async () => {
    if (!user) return;
    try {
      const response = await todosApi.listTags();
      if (response.success && response.data) {
        setAllTags(response.data);
      }
    } catch {
      // Non-critical — silently ignore
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchLists();
      fetchTodos();
      fetchCategories();
      fetchTags();
    } else {
      setTodos([]);
      setCategories([]);
      setLists([]);
      setAllTags([]);
    }
  }, [user, fetchLists, fetchTodos, fetchCategories, fetchTags]);

  const createTodo = async (data: TodoCreateInput): Promise<Todo> => {
    const response = await todosApi.create(data);
    if (response.success && response.data) {
      const newTodo = response.data.todo;
      setTodos((prev) => {
        if (prev.find((t) => t._id === newTodo._id)) return prev;
        return [newTodo, ...prev];
      });
      if (data.tags?.length) fetchTags();
      return newTodo;
    }
    throw new Error(response.error || 'Failed to create todo');
  };

  const duplicateTodo = async (
    id: string, listId: string, category?: string | null
  ): Promise<Todo> => {
    const response = await todosApi.duplicate(id, listId, category);
    if (response.success && response.data) {
      const newTodo = response.data.todo;
      setTodos((prev) => {
        if (prev.find((t) => t._id === newTodo._id)) return prev;
        return [newTodo, ...prev];
      });
      return newTodo;
    }
    throw new Error(response.error || 'Failed to duplicate todo');
  };

  const updateTodo = async (id: string, data: TodoUpdateInput): Promise<Todo> => {
    const prevTodos = [...todos];
    // Optimistic update - exclude category and extras since their input shapes differ from Todo
    setTodos((prev) =>
      prev.map((todo) => {
        if (todo._id !== id) return todo;
        const { category: _category, extras: _extras, ...restData } = data;
        return { ...todo, ...restData };
      })
    );

    try {
      const response = await todosApi.update(id, data);
      if (response.success && response.data) {
        const updatedTodo = response.data.todo;
        setTodos((prev) =>
          prev.map((todo) => (todo._id === id ? updatedTodo : todo))
        );
        if (data.tags) fetchTags();
        // Re-fetch to ensure grid sections reflect any date/category changes
        fetchTodos();
        return updatedTodo;
      }
      throw new Error(response.error || 'Failed to update todo');
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const deleteTodo = async (id: string): Promise<void> => {
    const prevTodos = [...todos];
    setTodos((prev) => prev.filter((todo) => todo._id !== id));

    try {
      const response = await todosApi.delete(id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete todo');
      }
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const toggleTodo = async (id: string): Promise<Todo> => {
    const prevTodos = [...todos];
    setTodos((prev) =>
      prev.map((todo) =>
        todo._id === id ? { ...todo, completed: !todo.completed } : todo
      )
    );

    try {
      const response = await todosApi.toggle(id);
      if (response.success && response.data) {
        const updatedTodo = response.data.todo;
        setTodos((prev) =>
          prev.map((todo) => (todo._id === id ? updatedTodo : todo))
        );
        return updatedTodo;
      }
      throw new Error(response.error || 'Failed to toggle todo');
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const shareTodo = async (id: string, email: string, permission: 'view' | 'edit'): Promise<void> => {
    const response = await todosApi.share(id, email, permission);
    if (response.success && response.data) {
      setTodos((prev) =>
        prev.map((todo) => (todo._id === id ? response.data!.todo : todo))
      );
    } else {
      throw new Error(response.error || 'Failed to share todo');
    }
  };

  const unshareTodo = async (id: string, userId: string): Promise<void> => {
    const response = await todosApi.unshare(id, userId);
    if (response.success) {
      setTodos((prev) =>
        prev.map((todo) =>
          todo._id === id
            ? {
                ...todo,
                sharedWith: todo.sharedWith.filter((s) => {
                  const sharedUserId = typeof s.userId === 'string' ? s.userId : s.userId._id;
                  return sharedUserId !== userId;
                }),
              }
            : todo
        )
      );
    } else {
      throw new Error(response.error || 'Failed to unshare todo');
    }
  };

  const reorderTodos = async (todosToReorder: Array<{ id: string; position: number }>): Promise<void> => {
    // Optimistic update
    const prevTodos = [...todos];
    setTodos((prev) => {
      const updated = [...prev];
      todosToReorder.forEach(({ id, position }) => {
        const idx = updated.findIndex((t) => t._id === id);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], position };
        }
      });
      return updated.sort((a, b) => a.position - b.position);
    });

    try {
      const response = await todosApi.reorder(todosToReorder);
      if (!response.success) {
        throw new Error(response.error || 'Failed to reorder todos');
      }
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const addSubtask = async (todoId: string, title: string): Promise<Todo> => {
    const response = await todosApi.addSubtask(todoId, title);
    if (response.success && response.data) {
      const updatedTodo = response.data.todo;
      setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
      return updatedTodo;
    }
    throw new Error(response.error || 'Failed to add subtask');
  };

  const toggleSubtask = async (todoId: string, subtaskId: string): Promise<Todo> => {
    // Optimistic update
    const prevTodos = [...todos];
    setTodos((prev) =>
      prev.map((todo) => {
        if (todo._id !== todoId) return todo;
        return {
          ...todo,
          subtasks: todo.subtasks.map((st) =>
            st._id === subtaskId ? { ...st, completed: !st.completed } : st
          ),
        };
      })
    );

    try {
      const response = await todosApi.toggleSubtask(todoId, subtaskId);
      if (response.success && response.data) {
        const updatedTodo = response.data.todo;
        setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
        return updatedTodo;
      }
      throw new Error(response.error || 'Failed to toggle subtask');
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const deleteSubtask = async (todoId: string, subtaskId: string): Promise<Todo> => {
    const prevTodos = [...todos];
    setTodos((prev) =>
      prev.map((todo) => {
        if (todo._id !== todoId) return todo;
        return {
          ...todo,
          subtasks: todo.subtasks.filter((st) => st._id !== subtaskId),
        };
      })
    );

    try {
      const response = await todosApi.deleteSubtask(todoId, subtaskId);
      if (response.success && response.data) {
        const updatedTodo = response.data.todo;
        setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
        return updatedTodo;
      }
      throw new Error(response.error || 'Failed to delete subtask');
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const renameSubtask = async (todoId: string, subtaskId: string, title: string): Promise<Todo> => {
    const prevTodos = [...todos];
    setTodos((prev) =>
      prev.map((todo) => {
        if (todo._id !== todoId) return todo;
        return {
          ...todo,
          subtasks: todo.subtasks.map((st) =>
            st._id === subtaskId ? { ...st, title } : st
          ),
        };
      })
    );

    try {
      const response = await todosApi.renameSubtask(todoId, subtaskId, title);
      if (response.success && response.data) {
        const updatedTodo = response.data.todo;
        setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
        return updatedTodo;
      }
      throw new Error(response.error || 'Failed to rename subtask');
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const reorderSubtasks = async (todoId: string, subtasks: Array<{ id: string; position: number }>): Promise<void> => {
    const prevTodos = [...todos];
    setTodos((prev) =>
      prev.map((todo) => {
        if (todo._id !== todoId) return todo;
        const updated = { ...todo, subtasks: [...todo.subtasks] };
        subtasks.forEach(({ id, position }) => {
          const st = updated.subtasks.find((s) => s._id === id);
          if (st) st.position = position;
        });
        return updated;
      })
    );

    try {
      const response = await todosApi.reorderSubtasks(todoId, subtasks);
      if (response.success && response.data) {
        const updatedTodo = response.data.todo;
        setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
      } else if (!response.success) {
        throw new Error(response.error || 'Failed to reorder subtasks');
      }
    } catch (err) {
      setTodos(prevTodos);
      throw err;
    }
  };

  const uploadTodoImage = async (todoId: string, file: File): Promise<Todo> => {
    const response = await todosApi.uploadImage(todoId, file);
    if (response.success && response.data) {
      const updatedTodo = response.data.todo;
      setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
      return updatedTodo;
    }
    throw new Error(response.error || 'Failed to upload image');
  };

  const deleteTodoImage = async (todoId: string, filename: string): Promise<Todo> => {
    const response = await todosApi.deleteImage(todoId, filename);
    if (response.success && response.data) {
      const updatedTodo = response.data.todo;
      setTodos((prev) => prev.map((t) => (t._id === todoId ? updatedTodo : t)));
      return updatedTodo;
    }
    throw new Error(response.error || 'Failed to delete image');
  };

  const createCategory = async (data: CategoryCreateInput): Promise<Category> => {
    const response = await categoriesApi.create(data);
    if (response.success && response.data) {
      const newCategory = response.data.category;
      setCategories((prev) => [...prev, newCategory].sort((a, b) => a.name.localeCompare(b.name)));
      return newCategory;
    }
    throw new Error(response.error || 'Failed to create category');
  };

  const updateCategory = async (id: string, data: CategoryUpdateInput): Promise<Category> => {
    const response = await categoriesApi.update(id, data);
    if (response.success && response.data) {
      const updatedCategory = response.data.category;
      setCategories((prev) =>
        prev
          .map((cat) => (cat._id === id ? updatedCategory : cat))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setTodos((prev) =>
        prev.map((todo) =>
          todo.category?._id === id ? { ...todo, category: updatedCategory } : todo
        )
      );
      return updatedCategory;
    }
    throw new Error(response.error || 'Failed to update category');
  };

  const deleteCategory = async (id: string): Promise<void> => {
    const response = await categoriesApi.delete(id);
    if (response.success) {
      setCategories((prev) => prev.filter((cat) => cat._id !== id));
      setTodos((prev) =>
        prev.map((todo) => (todo.category?._id === id ? { ...todo, category: null } : todo))
      );
      if (selectedCategory === id) {
        setSelectedCategory(null);
      }
    } else {
      throw new Error(response.error || 'Failed to delete category');
    }
  };

  const shareCategory = async (id: string, email: string, permission: 'view' | 'edit'): Promise<void> => {
    const response = await categoriesApi.share(id, email, permission);
    if (response.success && response.data) {
      setCategories((prev) =>
        prev.map((cat) => (cat._id === id ? response.data!.category : cat))
      );
    } else {
      throw new Error(response.error || 'Failed to share category');
    }
  };

  const unshareCategory = async (id: string, userId: string): Promise<void> => {
    const response = await categoriesApi.unshare(id, userId);
    if (response.success) {
      setCategories((prev) =>
        prev.map((cat) =>
          cat._id === id
            ? {
                ...cat,
                sharedWith: cat.sharedWith.filter((s) => {
                  const sharedUserId = typeof s.userId === 'string' ? s.userId : s.userId._id;
                  return sharedUserId !== userId;
                }),
              }
            : cat
        )
      );
    } else {
      throw new Error(response.error || 'Failed to unshare category');
    }
  };

  const createList = async (data: ListCreateInput): Promise<List> => {
    const response = await listsApi.create(data);
    if (response.success && response.data) {
      const newList = response.data.list;
      setLists((prev) => [...prev, newList].sort((a, b) => a.position - b.position));
      return newList;
    }
    throw new Error(response.error || 'Failed to create list');
  };

  const updateList = async (id: string, data: ListUpdateInput): Promise<List> => {
    const response = await listsApi.update(id, data);
    if (response.success && response.data) {
      const updatedList = response.data.list;
      setLists((prev) =>
        prev
          .map((list) => (list._id === id ? updatedList : list))
          .sort((a, b) => a.position - b.position)
      );
      return updatedList;
    }
    throw new Error(response.error || 'Failed to update list');
  };

  const deleteList = async (id: string): Promise<void> => {
    const response = await listsApi.delete(id);
    if (response.success) {
      setLists((prev) => prev.filter((list) => list._id !== id));
      // Remove todos and categories in this list from state
      setTodos((prev) => prev.filter((todo) => {
        const listId = typeof todo.listId === 'string' ? todo.listId : todo.listId?._id;
        return listId !== id;
      }));
      setCategories((prev) => prev.filter((cat) => {
        const listId = typeof cat.listId === 'string' ? cat.listId : cat.listId?._id;
        return listId !== id;
      }));
      if (selectedList === id) {
        setSelectedList(null);
      }
    } else {
      throw new Error(response.error || 'Failed to delete list');
    }
  };

  const shareList = async (id: string, email: string, permission: 'view' | 'edit'): Promise<void> => {
    const response = await listsApi.share(id, email, permission);
    if (response.success && response.data) {
      setLists((prev) =>
        prev.map((list) => (list._id === id ? response.data!.list : list))
      );
    } else {
      throw new Error(response.error || 'Failed to share list');
    }
  };

  const unshareList = async (id: string, userId: string): Promise<void> => {
    const response = await listsApi.unshare(id, userId);
    if (response.success) {
      setLists((prev) =>
        prev.map((list) =>
          list._id === id
            ? {
                ...list,
                sharedWith: list.sharedWith.filter((s) => {
                  const sharedUserId = typeof s.userId === 'string' ? s.userId : s.userId._id;
                  return sharedUserId !== userId;
                }),
              }
            : list
        )
      );
    } else {
      throw new Error(response.error || 'Failed to unshare list');
    }
  };

  const handleSSEEvent = useCallback((type: string, data: unknown) => {
    switch (type) {
      case 'todo:created':
        setTodos((prev) => {
          const todo = data as Todo;
          if (prev.find((t) => t._id === todo._id)) return prev;
          return [todo, ...prev];
        });
        break;
      case 'todo:updated':
        setTodos((prev) =>
          prev.map((todo) => (todo._id === (data as Todo)._id ? (data as Todo) : todo))
        );
        break;
      case 'todo:deleted':
        setTodos((prev) => prev.filter((todo) => todo._id !== (data as { id: string }).id));
        break;
      case 'todo:shared':
        setTodos((prev) => {
          const todo = data as Todo;
          const existing = prev.find((t) => t._id === todo._id);
          if (existing) {
            return prev.map((t) => (t._id === todo._id ? todo : t));
          }
          return [todo, ...prev];
        });
        break;
      case 'todo:unshared': {
        const { todoId, userId: unsharedUserId } = data as { todoId: string; userId: string };
        // Only remove if current user was the one unshared
        if (unsharedUserId === user?._id) {
          setTodos((prev) => prev.filter((todo) => todo._id !== todoId));
        } else {
          // Another user was unshared, update the sharedWith list
          setTodos((prev) =>
            prev.map((todo) =>
              todo._id === todoId
                ? { ...todo, sharedWith: todo.sharedWith.filter((s) => {
                    const uid = typeof s.userId === 'string' ? s.userId : s.userId._id;
                    return uid !== unsharedUserId;
                  })}
                : todo
            )
          );
        }
        break;
      }
      case 'todos:reordered': {
        const { todos: reorderedTodos } = data as { todos: Array<{ id: string; position: number }> };
        setTodos((prev) => {
          const updated = [...prev];
          reorderedTodos.forEach(({ id, position }) => {
            const idx = updated.findIndex((t) => t._id === id);
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], position };
            }
          });
          return updated.sort((a, b) => a.position - b.position);
        });
        break;
      }
      case 'category:created':
        setCategories((prev) => {
          const category = data as Category;
          if (prev.find((c) => c._id === category._id)) return prev;
          return [...prev, category].sort((a, b) => a.name.localeCompare(b.name));
        });
        break;
      case 'category:updated':
        setCategories((prev) =>
          prev
            .map((cat) => (cat._id === (data as Category)._id ? (data as Category) : cat))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        break;
      case 'category:deleted':
        setCategories((prev) => prev.filter((cat) => cat._id !== (data as { id: string }).id));
        break;
      case 'category:shared':
        setCategories((prev) => {
          const category = data as Category;
          const existing = prev.find((c) => c._id === category._id);
          if (existing) {
            return prev.map((c) => (c._id === category._id ? category : c));
          }
          return [...prev, category].sort((a, b) => a.name.localeCompare(b.name));
        });
        break;
      case 'category:unshared': {
        const { categoryId, userId: unsharedUserId } = data as { categoryId: string; userId: string };
        // Only remove if current user was the one unshared
        if (unsharedUserId === user?._id) {
          setCategories((prev) => prev.filter((cat) => cat._id !== categoryId));
        } else {
          // Another user was unshared, update the sharedWith list
          setCategories((prev) =>
            prev.map((cat) =>
              cat._id === categoryId
                ? { ...cat, sharedWith: cat.sharedWith.filter((s) => {
                    const uid = typeof s.userId === 'string' ? s.userId : s.userId._id;
                    return uid !== unsharedUserId;
                  })}
                : cat
            )
          );
        }
        break;
      }
      case 'list:created':
        setLists((prev) => {
          const list = data as List;
          if (prev.find((l) => l._id === list._id)) return prev;
          return [...prev, list].sort((a, b) => a.position - b.position);
        });
        break;
      case 'list:updated':
        setLists((prev) =>
          prev
            .map((list) => (list._id === (data as List)._id ? (data as List) : list))
            .sort((a, b) => a.position - b.position)
        );
        break;
      case 'list:deleted': {
        const deletedListId = (data as { id: string }).id;
        setLists((prev) => prev.filter((list) => list._id !== deletedListId));
        // Remove todos and categories in the deleted list
        setTodos((prev) => prev.filter((todo) => {
          const listId = typeof todo.listId === 'string' ? todo.listId : todo.listId?._id;
          return listId !== deletedListId;
        }));
        setCategories((prev) => prev.filter((cat) => {
          const listId = typeof cat.listId === 'string' ? cat.listId : cat.listId?._id;
          return listId !== deletedListId;
        }));
        break;
      }
      case 'list:shared':
        setLists((prev) => {
          const list = data as List;
          const existing = prev.find((l) => l._id === list._id);
          if (existing) {
            return prev.map((l) => (l._id === list._id ? list : l));
          }
          return [...prev, list].sort((a, b) => a.position - b.position);
        });
        // Also fetch categories and todos for the newly shared list
        fetchCategories();
        fetchTodos();
        break;
      case 'list:unshared': {
        const { listId: unsharedListId, userId: unsharedUserId } = data as { listId: string; userId: string };
        // Only remove if current user was the one unshared
        if (unsharedUserId === user?._id) {
          setLists((prev) => prev.filter((list) => list._id !== unsharedListId));
          // Remove todos and categories from unshared list
          setTodos((prev) => prev.filter((todo) => {
            const listId = typeof todo.listId === 'string' ? todo.listId : todo.listId?._id;
            return listId !== unsharedListId;
          }));
          setCategories((prev) => prev.filter((cat) => {
            const listId = typeof cat.listId === 'string' ? cat.listId : cat.listId?._id;
            return listId !== unsharedListId;
          }));
        } else {
          // Another user was unshared, update the sharedWith list
          setLists((prev) =>
            prev.map((list) =>
              list._id === unsharedListId
                ? { ...list, sharedWith: list.sharedWith.filter((s) => {
                    const uid = typeof s.userId === 'string' ? s.userId : s.userId._id;
                    return uid !== unsharedUserId;
                  })}
                : list
            )
          );
        }
        break;
      }
      case 'lists:reordered': {
        const { lists: reorderedLists } = data as { lists: Array<{ id: string; position: number }> };
        setLists((prev) => {
          const updated = [...prev];
          reorderedLists.forEach(({ id, position }) => {
            const idx = updated.findIndex((l) => l._id === id);
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], position };
            }
          });
          return updated.sort((a, b) => a.position - b.position);
        });
        break;
      }
    }
  }, [user?._id, fetchCategories, fetchTodos]);

  const clearError = () => setError(null);

  return (
    <TodoContext.Provider
      value={{
        todos,
        categories,
        lists,
        loading,
        error,
        pagination,
        selectedCategory,
        selectedList,
        viewMode,
        setSelectedCategory,
        setSelectedList,
        setViewMode,
        fetchTodos,
        fetchCategories,
        fetchLists,
        createTodo,
        duplicateTodo,
        updateTodo,
        deleteTodo,
        toggleTodo,
        shareTodo,
        unshareTodo,
        reorderTodos,
        addSubtask,
        toggleSubtask,
        deleteSubtask,
        renameSubtask,
        reorderSubtasks,
        uploadTodoImage,
        deleteTodoImage,
        createCategory,
        updateCategory,
        deleteCategory,
        shareCategory,
        unshareCategory,
        createList,
        updateList,
        deleteList,
        shareList,
        unshareList,
        handleSSEEvent,
        allTags,
        clearError,
      }}
    >
      {children}
    </TodoContext.Provider>
  );
}

export function useTodos() {
  const context = useContext(TodoContext);
  if (!context) {
    throw new Error('useTodos must be used within a TodoProvider');
  }
  return context;
}
